import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ============================================================================
// Slot group definitions (inline literals — required by the schema linter)
// ============================================================================

const ctaDef: SlotGroupDefinition = {
  label: 'CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Component
// ============================================================================

export function VirtualTourIntro() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const cta = useSlotGroup('virtualtourintro.cta', ctaDef);

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up w-full bg-[var(--vibe-color-background)] pt-[40px] pb-6 md:pb-9"
    >
      <div className="mx-auto px-3 sm:px-4 lg:px-6 text-center">
        <RichTextContent
          path="virtualtourintro.heading"
          label="Heading"
          defaultValue='<p>Tour <span data-type="field-token" data-field="property_name"></span> From Anywhere</p>'
          as="h2"
          className="font-heading text-balance text-[clamp(1.75rem,6vw,2.5rem)] font-semibold uppercase tracking-wide text-[var(--vibe-color-primary)] leading-[1.1] mb-6"
        />

        <div className="max-w-3xl mx-auto md:px-10 lg:px-0">
          <RichTextContent
            path="virtualtourintro.body"
            label="Body Text"
            defaultValue={`<p>Step inside <span data-type="field-token" data-field="property_name"></span> without leaving home. Our immersive 3D virtual tours let you explore floor plans, amenities, and community spaces at your own pace, from any device. When you're ready to see it in person, schedule a tour and we'll roll out the welcome mat.</p>`}
            as="div"
            className="font-body text-base leading-relaxed text-[var(--vibe-color-text-muted)] [&_p]:mb-4 [&_a]:text-primary [&_a]:underline"
          />
        </div>

        <div className="mt-6 flex justify-center">
          <EditableSlotGroup groupPath="virtualtourintro.cta" definition={ctaDef}>
            <a
              href={cta.url || '/schedule-a-tour'}
              target={cta._target || '_self'}
              className="vti-cta inline-flex items-center justify-center whitespace-nowrap px-6 py-3 font-heading text-sm md:px-4 md:tracking-[0.1em] lg:px-12 lg:py-4 lg:text-base lg:tracking-[0.15em] uppercase tracking-[0.15em] transition-all"
            >
              {cta.label || 'Schedule a Tour'}
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        /* Solid primary pill — matches the site-wide CTA treatment. */
        .vti-cta {
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary, #fff);
          border: 1.5px solid var(--vibe-color-primary);
          border-radius: 9999px;
        }
        .vti-cta:hover {
          background: color-mix(in srgb, var(--vibe-color-primary) 88%, #000);
          border-color: color-mix(in srgb, var(--vibe-color-primary) 88%, #000);
          transform: translateY(-1px);
          box-shadow: 0 6px 18px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'VirtualTourIntro',
  displayName: 'Virtual Tour Intro',
  tokens: {
    'virtualtourintro.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Tour <span data-type="field-token" data-field="property_name"></span> From Anywhere</p>',
    },
    'virtualtourintro.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultValue:
        '<p>Step inside <span data-type="field-token" data-field="property_name"></span> without leaving home. Our immersive 3D virtual tours let you explore floor plans, amenities, and community spaces at your own pace, from any device. When you\'re ready to see it in person, schedule a tour and we\'ll roll out the welcome mat.</p>',
    },
  },
  slotGroups: {
    'virtualtourintro.cta': ctaDef,
  },
};

(VirtualTourIntro as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default VirtualTourIntro;
